import type { BlogPostDraft } from '../../lib/blog';

export const post: BlogPostDraft = {
  slug: 'trouver-marques-ugc',
  lang: 'fr',
  title: 'Comment trouver des marques à contacter en UGC ? 5 pistes concrètes',
  description:
    'Découvre cinq méthodes pour trouver des marques adaptées à ton profil et construire une liste de prospection pertinente.',
  date: '2026-07-22',
  author: 'Équipe Leadsme',
  tags: ['prospection', 'ugc', 'marques', 'collaborations'],
  ctaTitleKey: 'blog.postCtaOrganizeTitle',
  ctaDescriptionKey: 'blog.postCtaOrganizeDescription',
  ctaButtonKey: 'landing.ctaButton',
  ctaTrustKey: 'blog.postCtaOrganizeTrust',
  content: `
<p class="blog-lead">
  Écrire un bon email de prospection est important. Encore faut-il savoir à quelle marque l'envoyer.
</p>
<p>
  Quand on débute dans l'UGC, on pense souvent qu'il faut contacter les entreprises les plus connues ou attendre qu'une marque publie un appel à créateurs. Pourtant, beaucoup de collaborations commencent simplement parce qu'un créateur a repéré une entreprise pertinente et pris l'initiative de lui écrire.
</p>
<p>
  L'objectif n'est pas de remplir un tableau avec des centaines de noms. Une liste plus courte, composée de marques qui correspondent réellement à ton profil, sera souvent plus efficace.
</p>
<p>
  Voici cinq pistes concrètes pour trouver des entreprises à prospecter sans les choisir complètement au hasard.
</p>

<h2>1. Commence par les marques que tu utilises déjà</h2>
<p>Regarde les produits et services que tu utilises régulièrement :</p>
<ul>
  <li>cosmétiques</li>
  <li>vêtements</li>
  <li>applications</li>
  <li>accessoires</li>
  <li>produits alimentaires</li>
  <li>équipements sportifs</li>
  <li>objets pour la maison</li>
</ul>
<p>
  Tu connais déjà leur fonctionnement, leurs points forts et les situations dans lesquelles tu les utilises. Il sera donc plus facile d'imaginer un contenu naturel et d'expliquer pourquoi tu souhaites travailler avec la marque.
</p>
<p>Au lieu d'écrire :</p>
<blockquote>
  <p>J'aime beaucoup votre marque et j'aimerais collaborer avec vous.</p>
</blockquote>
<p>Tu peux être plus précis :</p>
<blockquote>
  <p>J'utilise votre gourde pendant mes séances de sport depuis plusieurs mois. J'ai imaginé un format vidéo autour de sa facilité de transport et de son système de fermeture.</p>
</blockquote>
<p>La marque comprend immédiatement que ton message n'a pas été envoyé au hasard.</p>
<p>
  Ne te limite pas aux grandes enseignes. Les petites marques que tu achètes déjà peuvent être plus accessibles et avoir un véritable besoin de contenu, même si elles ne parlent pas encore publiquement d'UGC.
</p>

<h2>2. Observe les publicités et les collaborations UGC</h2>
<p>
  Une entreprise qui utilise déjà des vidéos de créateurs connaît généralement le fonctionnement de l'UGC. Elle peut avoir besoin de renouveler régulièrement ses contenus, de tester de nouvelles accroches ou de présenter ses produits avec différents profils.
</p>
<p>Lorsque tu vois une publicité sur Instagram ou TikTok, prends quelques secondes pour regarder :</p>
<ul>
  <li>le produit présenté</li>
  <li>le type de vidéo utilisé</li>
  <li>la personne choisie</li>
  <li>le ton du contenu</li>
  <li>les formats qui reviennent régulièrement</li>
</ul>
<p>L'objectif n'est pas de reproduire une vidéo existante. Cherche plutôt ce que tu pourrais apporter de différent :</p>
<ul>
  <li>un autre décor</li>
  <li>une autre tranche d'âge</li>
  <li>une démonstration plus détaillée</li>
  <li>une utilisation plus quotidienne</li>
  <li>un format face caméra</li>
  <li>une mise en situation en couple, entre amis ou en famille</li>
</ul>
<p>
  Tu peux également regarder les publications identifiées de la marque, les contenus qu'elle repartage et les portfolios de créateurs proches de ton univers.
</p>
<p>
  Si une entreprise a déjà travaillé avec plusieurs créateurs, cela ne signifie pas qu'elle n'a plus besoin de contenu. Au contraire, elle peut rechercher régulièrement de nouveaux profils.
</p>

<h2>3. Pense aux entreprises locales</h2>
<p>La prospection UGC ne concerne pas uniquement les marques nationales.</p>
<p>Autour de toi, de nombreuses entreprises peuvent avoir besoin de vidéos verticales pour Instagram ou TikTok :</p>
<ul>
  <li>restaurants</li>
  <li>hôtels</li>
  <li>salles de sport</li>
  <li>instituts de beauté</li>
  <li>boutiques</li>
  <li>activités de loisirs</li>
  <li>agences immobilières</li>
  <li>commerces indépendants</li>
</ul>
<p>
  Ces entreprises n'emploient pas toujours le terme « UGC ». Elles peuvent simplement chercher quelqu'un capable de présenter un lieu, filmer une expérience ou créer du contenu pour leurs réseaux sociaux.
</p>
<p>
  Ton avantage est de pouvoir te déplacer et produire des images dans un environnement réel.
</p>
<p>
  Avant de contacter une entreprise locale, regarde ce qu'elle publie déjà. Une belle adresse avec peu de vidéos immersives peut représenter une bonne opportunité.
</p>
<p>Plutôt que de proposer une « collaboration UGC » de manière générale, présente une idée concrète :</p>
<blockquote>
  <p>J'ai découvert votre brunch et je pense qu'un format immersif montrant l'arrivée, les plats et l'ambiance du lieu pourrait être intéressant pour vos réseaux sociaux.</p>
</blockquote>
<p>La proposition devient immédiatement plus facile à comprendre.</p>

<h2>4. Utilise LinkedIn pour trouver le bon contact</h2>
<p>Trouver une marque est une première étape. Il faut ensuite identifier la personne qui pourra réellement lire ta proposition.</p>
<p>Sur LinkedIn, recherche le nom de l'entreprise accompagné d'intitulés comme :</p>
<ul>
  <li>responsable marketing</li>
  <li>social media manager</li>
  <li>responsable influence</li>
  <li>content manager</li>
  <li>chef de projet communication</li>
  <li>brand manager</li>
</ul>
<p>
  Dans une petite entreprise, la bonne personne peut aussi être le fondateur, la fondatrice ou la personne chargée de la communication.
</p>
<p>
  Tu n'es pas obligé·e d'envoyer immédiatement ton argumentaire complet sur LinkedIn. La plateforme peut simplement te permettre de trouver un nom, de vérifier le poste de la personne et de personnaliser ton email.
</p>
<p>Lorsque tu ne trouves aucun contact, tu peux écrire à l'adresse générale de la marque avec une demande simple :</p>
<blockquote>
  <p>Bonjour, pourriez-vous m'indiquer la personne chargée des collaborations avec les créateurs de contenu ?</p>
</blockquote>
<p>
  Évite en revanche de contacter plusieurs salariés de la même entreprise le même jour. Choisis l'interlocuteur qui semble le plus pertinent, puis laisse-lui le temps de répondre.
</p>

<h2>5. Utilise les plateformes et les agences en complément</h2>
<p>
  Les plateformes et les agences spécialisées peuvent t'aider à trouver des marques qui recherchent déjà des créateurs UGC.
</p>
<p>
  Pour augmenter tes chances, ton profil doit permettre de comprendre rapidement ton univers, les types de vidéos que tu réalises, ta localisation et la qualité de ton contenu. Quelques exemples cohérents seront souvent plus efficaces qu'un portfolio rempli de vidéos sans véritable direction.
</p>
<p>
  Ces plateformes restent toutefois un complément à la prospection directe. Les campagnes peuvent recevoir beaucoup de candidatures et les budgets proposés ne correspondent pas toujours à tes tarifs.
</p>

<h2>Pour résumer</h2>
<p>Trouver des marques à contacter demande surtout d'apprendre à observer.</p>
<p>
  Commence par les produits que tu utilises, les publicités que tu vois, les entreprises proches de chez toi, les contacts présents sur LinkedIn et les opportunités proposées par les plateformes spécialisées.
</p>
<p>
  L'objectif n'est pas d'accumuler le plus grand nombre de marques possible, mais d'identifier celles pour lesquelles tu peux réellement imaginer un contenu pertinent.
</p>
<p>
  Une fois ta sélection prête, tu peux t'appuyer sur nos <a href="/blog/email-prospection-ugc">7 exemples d'emails de prospection UGC</a> pour rédiger un message adapté à chaque entreprise.
</p>
`,
};
